import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { catchError, finalize, forkJoin, interval, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  BulkPlanningProfile,
  BulkPlanningLaneRule,
  BulkPlanningProfileMatchResponse,
  BulkPlanningAdjustmentAudit,
  BulkPlanningAllocationBasis,
  BulkPlanningResourceOptions,
  BulkPlanningRun,
  BulkPlanningRunRelease,
  BulkPlanningScenario,
  BulkPlanningSelectionFilter,
  BulkPlanningSelectionSpec,
  LocationZone,
  TransportOrderRelease,
  TransportExecutionLink,
  TransportOptimizationPlan,
  TransportOptimizationPlanRoute,
  TransportOptimizationPlanRouteStop
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

interface RouteStopGroup {
  key: string;
  label: string;
  stopType: string;
  releaseCount: number;
  releaseIds: number[];
  activities: TransportOptimizationPlanRouteStop[];
}

@Component({
  selector: 'app-bulk-planning-workbench',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TableModule,
    TagModule,
    TabsModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './bulk-planning-workbench.component.html',
  styleUrls: ['./bulk-planning-workbench.component.css']
})
export class BulkPlanningWorkbenchComponent implements OnInit, OnDestroy {
  profiles: BulkPlanningProfile[] = [];
  runs: BulkPlanningRun[] = [];
  locations: Location[] = [];
  locationZones: LocationZone[] = [];
  run: BulkPlanningRun | null = null;
  selectedPlan: TransportOptimizationPlan | null = null;
  selectedScenarioId: number | null = null;
  selectedProfileId: number | null = null;
  runName = '';
  horizonStart = this.startOfToday();
  horizonEnd = this.addDays(this.startOfToday(), 2);
  selection: BulkPlanningSelectionSpec = { mode: 'EXPLICIT_IDS', releaseIds: [] };
  profileDraft: BulkPlanningProfile;
  profileEditorOpen = false;
  isLoading = false;
  isSaving = false;
  message = '';
  error = '';
  plannerCopyReason = '';
  expandedRouteId: number | null = null;
  resourceOptions = new Map<number, BulkPlanningResourceOptions>();
  resourceOptionsLoading = new Set<number>();
  resourceOptionsErrors = new Map<number, string>();
  selectedDriverSnapshots = new Map<number, number>();
  selectedVehicleSnapshots = new Map<number, number>();
  adjustmentReasons = new Map<number, string>();
  buyingPrices = new Map<number, number | null>();
  sellingPrices = new Map<number, number | null>();
  allocationBases = new Map<number, BulkPlanningAllocationBasis>();
  stopDrafts = new Map<number, TransportOptimizationPlanRouteStop[]>();
  targetRoutes = new Map<string, number | null>();
  adjustmentAudit: BulkPlanningAdjustmentAudit[] = [];
  validationMessages: string[] = [];
  appliedExecutionLinks: TransportExecutionLink[] = [];
  profileMatchPreview: BulkPlanningProfileMatchResponse | null = null;
  isMatchingProfiles = false;

  readonly objectiveOptions: SelectOption[] = [
    { label: 'Service first', value: 'SERVICE_THEN_UTILIZATION' },
    { label: 'Lower truck count', value: 'MINIMIZE_TRUCKS' },
    { label: 'Lower distance', value: 'MINIMIZE_DISTANCE' }
  ];
  readonly fleetScopeOptions: SelectOption[] = [
    { label: 'Owned and sister-company fleet', value: 'OWN_AND_SISTER' },
    { label: 'Owned fleet only', value: 'OWNED' }
  ];
  readonly resourceSourceOptions: SelectOption[] = [
    { label: 'Mobilizations and FIFO availability', value: 'HYBRID' },
    { label: 'FIFO availability only', value: 'AVAILABLE_FIFO' },
    { label: 'Mobilizations only', value: 'MOBILIZED_ONLY' }
  ];
  readonly rankingStrictnessOptions: SelectOption[] = [
    { label: 'Soft FIFO preference', value: 'SOFT' },
    { label: 'Strict FIFO priority', value: 'STRICT' }
  ];
  readonly allocationBasisOptions: SelectOption<BulkPlanningAllocationBasis>[] = [
    { label: 'Automatic best available metric', value: 'AUTO' },
    { label: 'Weight', value: 'WEIGHT' },
    { label: 'Volume', value: 'VOLUME' },
    { label: 'Pallets', value: 'PALLETS' },
    { label: 'Quantity', value: 'QUANTITY' },
    { label: 'Units', value: 'UNITS' },
    { label: 'Equal split', value: 'EQUAL' },
    { label: 'Weight carried distance', value: 'WEIGHT_CARRIED_DISTANCE' },
    { label: 'Volume carried distance', value: 'VOLUME_CARRIED_DISTANCE' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly service: TransportPlanningService,
    private readonly locationService: LocationService,
    private readonly session: SessionStorageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.profileDraft = this.newProfile();
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const runId = Number(params.get('runId'));
      this.run = null;
      this.selectedPlan = null;
      this.selectedScenarioId = null;
      this.appliedExecutionLinks = [];
      this.loadBaseData(Number.isFinite(runId) && runId > 0 ? runId : null);
    });
    interval(4000).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.run && ['QUEUED', 'RUNNING'].includes(this.run.status)
        ? this.service.getBulkPlanningRun(this.run.runId, this.companyId())
        : of(null))
    ).subscribe(run => {
      if (run) this.applyRun(run);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isNew(): boolean {
    return this.router.url.split('?')[0].endsWith('/new');
  }

  get isList(): boolean {
    return !this.isNew && !this.run;
  }

  get activeScenario(): BulkPlanningScenario | null {
    return this.run?.scenarios?.find(item => item.scenarioId === this.selectedScenarioId) ?? null;
  }

  get isAdjustedScenario(): boolean {
    return this.activeScenario?.scenarioType === 'PLANNER_ADJUSTED';
  }

  get blockingResults(): BulkPlanningRunRelease[] {
    return (this.run?.releases ?? []).filter(item => ['BLOCKED', 'UNPLANNED'].includes(item.status));
  }

  get unresolvedBlockingCount(): number {
    return this.blockingResults.filter(item => !item.explicitlyExcluded).length;
  }

  get canApprove(): boolean {
    return !!this.activeScenario
      && ['REVIEW_READY', 'PARTIAL'].includes(this.activeScenario.status)
      && this.unresolvedBlockingCount === 0
      && this.run?.status !== 'APPLIED';
  }

  get hasDemandSelection(): boolean {
    return this.selection.mode === 'FILTER_SNAPSHOT' || !!this.selection.releaseIds?.length;
  }

  get selectedProfile(): BulkPlanningProfile | null {
    return this.profiles.find(profile => profile.profileId === this.selectedProfileId) ?? null;
  }

  get hasEligibleProfileMatch(): boolean {
    return this.profileMatchPreview?.groups.some(group => group.eligibleCount > 0) ?? false;
  }

  get zonedLocationOptions(): SelectOption<number>[] {
    return this.locations
      .filter(location => !!location.id && !!location.locationZoneId && this.hasCoordinates(location))
      .map(location => ({
        value: location.id,
        label: `${location.name || location.shortDescription || `Location ${location.id}`} | ${this.locationZoneName(location)}`
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  loadBaseData(runId: number | null): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.error = 'Choose an owning company before opening bulk planning.';
      return;
    }
    this.isLoading = true;
    this.error = '';
    forkJoin({
      profiles: this.service.getBulkPlanningProfiles(companyId).pipe(catchError(() => of([]))),
      runs: this.service.getBulkPlanningRuns(companyId).pipe(catchError(() => of([]))),
      locationZones: this.service.getLocationZones(companyId, true).pipe(catchError(() => of([]))),
      locations: this.locationService.getLocationsByCompanyId(0, 1000, companyId)
        .pipe(catchError(() => of({ items: [] } as any))),
      run: runId
        ? this.service.getBulkPlanningRun(runId, companyId).pipe(catchError(error => {
            this.error = this.errorText(error, 'Could not load the bulk planning run.');
            return of(null);
          }))
        : of(null)
    }).pipe(finalize(() => this.isLoading = false)).subscribe(result => {
      this.profiles = (result.profiles ?? []).map(profile => this.normalizeProfile(profile));
      this.runs = result.runs ?? [];
      this.locationZones = result.locationZones ?? [];
      this.locations = result.locations.items ?? [];
      this.selectedProfileId ||= this.profiles.find(profile => profile.active)?.profileId ?? null;
      this.profileEditorOpen = this.isNew && !this.profiles.length;
      if (result.run) this.applyRun(result.run);
      if (this.isNew) {
        this.readSelectionFromQuery();
        if (this.hasDemandSelection) this.previewProfileMatches();
      }
    });
  }

  previewProfileMatches(): void {
    if (!this.hasDemandSelection || this.horizonEnd <= this.horizonStart) return;
    this.isMatchingProfiles = true;
    this.error = '';
    this.service.matchBulkPlanningProfiles({
      owningCompanyId: this.companyId(),
      horizonStart: this.horizonStart,
      horizonEnd: this.horizonEnd,
      selection: this.selection
    }).pipe(finalize(() => this.isMatchingProfiles = false)).subscribe({
      next: preview => {
        this.profileMatchPreview = preview;
        if (preview.groups.length === 1) this.selectedProfileId = preview.groups[0].profileId;
      },
      error: error => this.error = this.errorText(error, 'Could not match releases to planning profiles.')
    });
  }

  startMatchedPlans(): void {
    if (!this.profileMatchPreview?.groups.some(group => group.eligibleCount > 0)) {
      this.error = 'No matched and eligible releases are available to plan.';
      return;
    }
    this.isSaving = true;
    this.error = '';
    this.service.createMatchedBulkPlanningRuns({
      owningCompanyId: this.companyId(),
      runName: this.runName.trim() || undefined,
      triggerType: 'INTERACTIVE',
      horizonStart: this.horizonStart,
      horizonEnd: this.horizonEnd,
      selection: this.selection
    }).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: result => {
        this.profileMatchPreview = result.match;
        if (result.runs.length === 1) {
          this.router.navigate(['/transport-planning/bulk-planning', result.runs[0].runId]);
          return;
        }
        this.runs = [...result.runs, ...this.runs];
        this.message = `${result.runs.length} matched bulk planning runs started.`;
      },
      error: error => this.error = this.errorText(error, 'Could not start matched bulk planning runs.')
    });
  }

  createRun(): void {
    if (this.selection.mode === 'EXPLICIT_IDS' && !this.selection.releaseIds?.length) {
      this.error = 'Select one or more releases from the Release Pool before starting bulk planning.';
      return;
    }
    if (!this.selectedProfileId) {
      this.error = 'Select or create a planning profile.';
      return;
    }
    if (this.horizonEnd <= this.horizonStart) {
      this.error = 'Planning horizon end must be after its start.';
      return;
    }
    this.isSaving = true;
    this.error = '';
    this.service.createBulkPlanningRun({
      owningCompanyId: this.companyId(),
      profileId: this.selectedProfileId,
      runName: this.runName.trim() || undefined,
      triggerType: 'INTERACTIVE',
      horizonStart: this.horizonStart,
      horizonEnd: this.horizonEnd,
      selection: this.selection
    }).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: run => this.router.navigate(['/transport-planning/bulk-planning', run.runId]),
      error: error => this.error = this.errorText(error, 'Could not start the bulk planning run.')
    });
  }

  saveProfile(): void {
    if (!this.profileDraft.name.trim() || !this.profileDraft.code.trim()) {
      this.error = 'Profile name and code are required.';
      return;
    }
    const laneRules = this.profileDraft.laneRules ?? [];
    if (this.profileDraft.autoMatchEnabled && !laneRules.length) {
      this.error = 'Add at least one directional zone lane before enabling automatic matching.';
      return;
    }
    if (laneRules.some(rule => !rule.pickupZoneIds.length || !rule.dropoffZoneIds.length)) {
      this.error = 'Each lane requires at least one pickup zone and one drop-off zone.';
      return;
    }
    if (this.profileDraft.maximumReleasesPerRoute < 1
      || this.profileDraft.maximumServiceStopsPerRoute < 2
      || this.profileDraft.maximumFifoCandidates < 1) {
      this.error = 'Route and FIFO candidate limits must be greater than zero.';
      return;
    }
    this.isSaving = true;
    this.error = '';
    const request = {
      ...this.profileDraft,
      owningCompanyId: this.companyId(),
      name: this.profileDraft.name.trim(),
      code: this.profileDraft.code.trim().toUpperCase()
    };
    const save = request.profileId
      ? this.service.updateBulkPlanningProfile(request)
      : this.service.createBulkPlanningProfile(request);
    save.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: profile => {
        profile = this.normalizeProfile(profile);
        const existingIndex = this.profiles.findIndex(item => item.profileId === profile.profileId);
        if (existingIndex >= 0) {
          this.profiles = this.profiles.map(item => item.profileId === profile.profileId ? profile : item);
        } else {
          this.profiles = [...this.profiles, profile];
        }
        this.selectedProfileId = profile.profileId ?? null;
        this.profileDraft = this.newProfile();
        this.profileEditorOpen = false;
        this.message = 'Planning profile saved.';
        if (this.hasDemandSelection) this.previewProfileMatches();
      },
      error: error => this.error = this.errorText(error, 'Could not save the planning profile.')
    });
  }

  editSelectedProfile(): void {
    const selected = this.profiles.find(profile => profile.profileId === this.selectedProfileId);
    this.profileDraft = selected ? this.normalizeProfile(selected) : this.newProfile();
    this.profileEditorOpen = true;
  }

  addLaneRule(): void {
    this.profileDraft.laneRules = [...(this.profileDraft.laneRules ?? []), this.emptyLaneRule()];
  }

  removeLaneRule(index: number): void {
    this.profileDraft.laneRules = this.profileDraft.laneRules.filter((_, ruleIndex) => ruleIndex !== index);
  }

  profileScopeLabel(profile: BulkPlanningProfile): string {
    const count = profile.laneRules?.length ?? 0;
    if (!count) return 'Unscoped, manual selection only';
    return `${count} directional lane${count === 1 ? '' : 's'}`;
  }

  openRun(run: BulkPlanningRun): void {
    this.router.navigate(['/transport-planning/bulk-planning', run.runId]);
  }

  selectScenario(scenario: BulkPlanningScenario): void {
    this.selectedScenarioId = scenario.scenarioId;
    this.loadSelectedPlan();
  }

  generateScenario(objective: string): void {
    if (!this.run) return;
    const sequence = (this.run.scenarios?.length ?? 0) + 1;
    const label = this.objectiveOptions.find(option => option.value === objective)?.label ?? objective;
    this.isSaving = true;
    this.error = '';
    this.service.createBulkPlanningScenario(this.run.runId, this.companyId(), {
      scenarioCode: `SCN-${sequence}`,
      scenarioName: label,
      objective
    }).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: run => {
        this.applyRun(run);
        const created = run.scenarios?.[run.scenarios.length - 1];
        if (created) this.selectScenario(created);
      },
      error: error => this.error = this.errorText(error, 'Could not generate the comparison scenario.')
    });
  }

  createPlannerCopy(): void {
    if (!this.run || !this.activeScenario) return;
    if (!this.plannerCopyReason.trim()) {
      this.error = 'Enter a reason before creating an adjusted scenario.';
      return;
    }
    this.isSaving = true;
    this.error = '';
    this.service.createBulkPlanningPlannerCopy(this.run.runId, this.activeScenario.scenarioId, this.companyId(), {
      scenarioName: `${this.activeScenario.scenarioName} - Planner adjusted`,
      reason: this.plannerCopyReason.trim(),
      actorCustomerId: this.actorCustomerId(),
      expectedVersion: this.activeScenario.version
    }).pipe(
      switchMap(plan => this.service.getBulkPlanningRun(this.run!.runId, this.companyId())
        .pipe(switchMap(run => of({ run, plan })))),
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: ({ run, plan }) => {
        this.applyRun(run);
        const adjusted = run.scenarios?.find(item => item.planId === plan.planId);
        if (adjusted) this.selectScenario(adjusted);
        this.plannerCopyReason = '';
        this.message = 'Planner-adjusted scenario created. The optimizer baseline remains unchanged.';
      },
      error: error => this.error = this.errorText(error, 'Could not create the adjusted scenario.')
    });
  }

  toggleRouteDetails(route: TransportOptimizationPlanRoute): void {
    if (!route.routeId) return;
    this.expandedRouteId = this.expandedRouteId === route.routeId ? null : route.routeId;
    if (this.expandedRouteId == null) return;
    this.adjustmentReasons.set(route.routeId, this.adjustmentReasons.get(route.routeId) || '');
    this.buyingPrices.set(route.routeId, route.buyingPrice ?? null);
    this.sellingPrices.set(route.routeId, route.sellingPrice ?? null);
    this.allocationBases.set(route.routeId,
      (route.projectAllocations?.[0]?.allocationBasisType as BulkPlanningAllocationBasis) || 'AUTO');
    this.stopDrafts.set(route.routeId, [...(route.stops ?? [])].sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0)));
    this.loadResourceOptions(route);
    this.loadAdjustmentAudit();
  }

  loadResourceOptions(route: TransportOptimizationPlanRoute): void {
    if (!this.run || !this.activeScenario || !route.routeId) return;
    const routeId = route.routeId;
    this.resourceOptionsLoading.add(routeId);
    this.resourceOptionsErrors.delete(routeId);
    this.service.getBulkPlanningResourceOptions(this.run.runId, this.activeScenario.scenarioId,
      routeId, this.companyId()).pipe(
        finalize(() => this.resourceOptionsLoading.delete(routeId))
      ).subscribe({
      next: options => {
        options.drivers = options.drivers.map(option => ({
          ...option,
          disabled: !option.compatible,
          label: option.compatible ? option.label : `${option.label} - ${option.incompatibilityReasons?.[0] || 'Unavailable'}`
        }));
        options.vehicles = options.vehicles.map(option => ({
          ...option,
          disabled: !option.compatible,
          label: option.compatible ? option.label : `${option.label} - ${option.incompatibilityReasons?.[0] || 'Unavailable'}`
        }));
        this.resourceOptions.set(routeId, options);
        this.selectedDriverSnapshots.set(routeId, options.currentDriverSnapshotId ?? route.vehicleSnapshotId!);
        this.selectedVehicleSnapshots.set(routeId, options.currentVehicleSnapshotId ?? route.vehicleSnapshotId!);
      },
      error: error => {
        this.resourceOptions.delete(routeId);
        this.resourceOptionsErrors.set(routeId,
          this.errorText(error, 'Could not load driver and vehicle alternatives.'));
      }
    });
  }

  eligibleDriverCount(routeId: number): number {
    return (this.resourceOptions.get(routeId)?.drivers ?? []).filter(option => option.compatible).length;
  }

  eligibleVehicleCount(routeId: number): number {
    return (this.resourceOptions.get(routeId)?.vehicles ?? []).filter(option => option.compatible).length;
  }

  resourceEligibilityCriteria(): string {
    return 'Same planning snapshot and fleet scope; ready for assignment; available for the route window; '
      + 'not assigned to another route or overlapping applied work; and paired with a vehicle that satisfies '
      + 'capacity buffer, route duration, release-count, and physical-stop limits.';
  }

  selectRecommendedPair(routeId: number, driverSnapshotId: number, vehicleSnapshotId: number): void {
    this.selectedDriverSnapshots.set(routeId, driverSnapshotId);
    this.selectedVehicleSnapshots.set(routeId, vehicleSnapshotId);
  }

  saveRouteResources(route: TransportOptimizationPlanRoute): void {
    if (!this.run || !this.activeScenario || !route.routeId) return;
    const reason = this.routeReason(route.routeId);
    if (!reason) return;
    this.isSaving = true;
    this.service.changeBulkPlanningRouteResources(this.run.runId, this.activeScenario.scenarioId,
      route.routeId, this.companyId(), {
        driverSnapshotId: this.selectedDriverSnapshots.get(route.routeId),
        vehicleSnapshotId: this.selectedVehicleSnapshots.get(route.routeId),
        expectedRouteVersion: route.version,
        reason,
        actorCustomerId: this.actorCustomerId()
      }).pipe(finalize(() => this.isSaving = false)).subscribe({
        next: plan => this.afterPlanAdjustment(plan, 'Driver and vehicle selection updated.'),
        error: error => this.error = this.errorText(error, 'The selected resources are not valid for this route.')
      });
  }

  saveRouteFinancials(route: TransportOptimizationPlanRoute): void {
    if (!this.run || !this.activeScenario || !route.routeId) return;
    const reason = this.routeReason(route.routeId);
    if (!reason) return;
    const allocations = (route.projectAllocations ?? []).map(item => ({
      logisticsProjectId: item.logisticsProjectId,
      allocationPercentage: item.allocationPercentage
    }));
    this.isSaving = true;
    this.service.saveBulkPlanningRouteFinancials(this.run.runId, this.activeScenario.scenarioId,
      route.routeId, this.companyId(), {
        buyingPrice: this.buyingPrices.get(route.routeId),
        sellingPrice: route.billingMode === 'CONSOLIDATED_ZERO_SELL' ? 0 : this.sellingPrices.get(route.routeId),
        allocationBasis: this.allocationBases.get(route.routeId) || 'AUTO',
        projectAllocations: allocations,
        expectedRouteVersion: route.version,
        reason,
        actorCustomerId: this.actorCustomerId()
      }).pipe(finalize(() => this.isSaving = false)).subscribe({
        next: plan => this.afterPlanAdjustment(plan, 'Route price and project allocation updated.'),
        error: error => this.error = this.errorText(error, 'Could not save route financials.')
      });
  }

  moveRelease(route: TransportOptimizationPlanRoute, releaseId?: number): void {
    if (!this.run || !this.activeScenario || !route.routeId || !releaseId) return;
    const reason = this.routeReason(route.routeId);
    if (!reason) return;
    const key = `${route.routeId}:${releaseId}`;
    const targetRouteId = this.targetRoutes.get(key) ?? undefined;
    const target = this.selectedPlan?.routes?.find(item => item.routeId === targetRouteId);
    this.isSaving = true;
    this.service.moveBulkPlanningRelease(this.run.runId, this.activeScenario.scenarioId, releaseId,
      this.companyId(), {
        sourceRouteId: route.routeId,
        targetRouteId,
        expectedSourceRouteVersion: route.version,
        expectedTargetRouteVersion: target?.version,
        reason,
        actorCustomerId: this.actorCustomerId()
      }).pipe(finalize(() => this.isSaving = false)).subscribe({
        next: plan => this.afterPlanAdjustment(plan, 'Release moved to the selected route.'),
        error: error => this.error = this.errorText(error, 'Could not move the release.')
      });
  }

  moveStop(routeId: number, index: number, direction: -1 | 1): void {
    if (!this.canMovePhysicalStop(routeId, index, direction)) return;
    const stops = this.physicalStopDrafts(routeId);
    const target = index + direction;
    [stops[index], stops[target]] = [stops[target], stops[index]];
    this.stopDrafts.set(routeId, stops.flatMap(stop => stop.activities));
  }

  saveStopSequence(route: TransportOptimizationPlanRoute): void {
    if (!this.run || !this.activeScenario || !route.routeId) return;
    const reason = this.routeReason(route.routeId);
    if (!reason) return;
    this.isSaving = true;
    this.service.resequenceBulkPlanningStops(this.run.runId, this.activeScenario.scenarioId, route.routeId,
      this.companyId(), {
        stops: (this.stopDrafts.get(route.routeId) ?? []).map(stop => ({
          releaseId: stop.releaseId,
          stopType: stop.stopType
        })),
        expectedRouteVersion: route.version,
        reason,
        actorCustomerId: this.actorCustomerId()
      }).pipe(finalize(() => this.isSaving = false)).subscribe({
        next: plan => this.afterPlanAdjustment(plan, 'Route stop sequence updated.'),
        error: error => this.error = this.errorText(error, 'Could not save the stop sequence.')
      });
  }

  validateAdjustedScenario(): void {
    if (!this.run || !this.activeScenario || !this.isAdjustedScenario) return;
    this.isSaving = true;
    this.validationMessages = [];
    this.service.validateBulkPlanningScenario(this.run.runId, this.activeScenario.scenarioId, this.companyId(), {
      expectedVersion: this.activeScenario.version,
      reason: 'Planner completed manual route review',
      actorCustomerId: this.actorCustomerId()
    }).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: result => {
        this.validationMessages = result.issues.map(issue => issue.message);
        this.message = result.valid ? 'Adjusted scenario validated and ready for approval.' : '';
        this.refreshRun();
      },
      error: error => this.error = this.errorText(error, 'Scenario validation failed.')
    });
  }

  routeReleases(route: TransportOptimizationPlanRoute): TransportOrderRelease[] {
    const releases = new Map((this.selectedPlan?.releases ?? [])
      .filter(release => release.releaseId != null)
      .map(release => [Number(release.releaseId), release]));
    return this.routeReleaseIds(route)
      .map(releaseId => releases.get(releaseId) ?? {
        releaseId,
        owningCompanyId: this.companyId(),
        sourceType: 'UNKNOWN'
      });
  }

  releaseLabel(release?: TransportOrderRelease): string {
    return release?.releaseNumber || release?.sourceNumber || `Release #${release?.releaseId || '-'}`;
  }

  releaseReferenceContext(release?: TransportOrderRelease): string {
    if (!release) return 'Source reference not set';
    const rawProject = this.cleanReleaseReference(release.logisticsProjectNumber);
    const rawSource = this.cleanReleaseReference(release.posmDeliveryScheduleNumber || release.sourceNumber);
    const project = this.projectReference(rawProject || rawSource)
      || (release.logisticsProjectId ? `Project #${release.logisticsProjectId}` : '');
    let source = rawSource;
    if (project && source?.startsWith(`${project}/`)) source = source.substring(project.length + 1);
    if (source === project || source === release.releaseNumber) source = '';
    const embeddedLeg = `${release.logisticsProjectNumber || ''} ${release.sourceNumber || ''}`
      .match(/Leg\s+(\d+)\s+of\s+(\d+)/i);
    const legSequence = release.legSequence ?? (embeddedLeg ? Number(embeddedLeg[1]) : undefined);
    const legCount = release.legCount ?? (embeddedLeg ? Number(embeddedLeg[2]) : undefined);
    const leg = legSequence && legCount ? `Leg ${legSequence} of ${legCount}` : '';
    return [...new Set([project, source, leg].filter(Boolean))].join(' | ') || 'Source reference not set';
  }

  releaseCustomerContext(release?: TransportOrderRelease): string {
    if (!release) return 'Customer not set';
    const customer = release.crmClientName || release.companyName || release.customerName || 'Customer not set';
    return release.crmAccountName ? `${customer} / ${release.crmAccountName}` : customer;
  }

  releaseLoadContext(release?: TransportOrderRelease): string {
    const lines = release?.lines ?? [];
    if (lines.length === 0) return 'Item details not set';
    const quantity = lines.reduce((total, line) => total + Number(line.quantity ?? line.units ?? 0), 0);
    const uoms = [...new Set(lines.map(line => line.uom || line.tenantUomName).filter(Boolean))];
    const first = lines[0];
    const item = first.itemNumber || first.itemCode || first.skuNumber || first.itemName || 'Item not set';
    const itemName = first.itemName && first.itemName !== item ? ` - ${first.itemName}` : '';
    const quantityText = quantity > 0 ? `${this.compactNumber(quantity)} ${uoms.length === 1 ? uoms[0] : 'units'}` : '';
    if (lines.length === 1) {
      const sku = first.skuNumber && first.skuNumber !== item ? `SKU ${first.skuNumber}` : '';
      const size = first.itemSize ? `Size ${first.itemSize}` : '';
      return [quantityText, `${item}${itemName}`, sku, size].filter(Boolean).join(' | ');
    }
    return [quantityText, `${item}${itemName} +${lines.length - 1} more line${lines.length === 2 ? '' : 's'}`]
      .filter(Boolean).join(' | ');
  }

  routeOptions(sourceRouteId?: number): SelectOption<number | null>[] {
    const routes = (this.selectedPlan?.routes ?? []).filter(route => route.routeId !== sourceRouteId)
      .map(route => ({ label: `Route ${route.routeSequence} - ${route.vehiclePlateNumber || route.vehicleLabel || 'vehicle'}`,
        value: route.routeId! }));
    return [{ label: 'Create a new route', value: null }, ...routes];
  }

  allocationTotal(route: TransportOptimizationPlanRoute): number {
    return (route.projectAllocations ?? []).reduce((total, item) => total + Number(item.allocationPercentage || 0), 0);
  }

  setExcluded(result: BulkPlanningRunRelease, excluded: boolean): void {
    if (!this.run) return;
    this.service.setBulkPlanningReleaseExcluded(
      this.run.runId,
      this.companyId(),
      result.releaseId,
      excluded,
      excluded ? 'Explicitly excluded by planner during review' : 'Restored by planner during review',
      this.isAdjustedScenario ? this.activeScenario?.scenarioId : undefined,
      this.isAdjustedScenario ? this.activeScenario?.version : undefined
    ).subscribe({
      next: run => this.applyRun(run),
      error: error => this.error = this.errorText(error, 'Could not update the planning exception.')
    });
  }

  toggleRouteLock(route: TransportOptimizationPlanRoute): void {
    if (!this.run || !this.activeScenario || !route.routeId) return;
    this.service.setBulkPlanningRouteLocked(
      this.run.runId,
      this.activeScenario.scenarioId,
      route.routeId,
      this.companyId(),
      !route.locked,
      route.version
    ).subscribe({
      next: run => {
        this.applyRun(run);
        this.loadSelectedPlan();
      },
      error: error => this.error = this.errorText(error, 'The route lock could not be changed.')
    });
  }

  reoptimizeUnlocked(): void {
    if (!this.run || !this.activeScenario) return;
    this.isSaving = true;
    this.error = '';
    this.service.reoptimizeBulkPlanningScenario(
      this.run.runId,
      this.activeScenario.scenarioId,
      this.companyId()
    ).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: run => this.applyRun(run),
      error: error => this.error = this.errorText(error, 'Unlocked routes could not be reoptimized.')
    });
  }

  retryRun(): void {
    if (!this.run) return;
    this.isSaving = true;
    this.error = '';
    this.service.retryBulkPlanningRun(this.run.runId, this.companyId())
      .pipe(finalize(() => this.isSaving = false))
      .subscribe({
        next: run => this.applyRun(run),
        error: error => this.error = this.errorText(error, 'The failed run could not be retried.')
      });
  }

  approve(): void {
    if (!this.run || !this.activeScenario || !this.canApprove) return;
    this.isSaving = true;
    this.error = '';
    this.service.approveAndApplyBulkPlanningScenario(
      this.run.runId,
      this.activeScenario.scenarioId,
      this.companyId()
    ).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: response => {
        this.appliedExecutionLinks = response.execution?.executionLinks ?? [];
        const shipmentCount = response.execution?.shipmentIds?.length ?? 0;
        const deliveryCount = response.execution?.deliveryIds?.length ?? 0;
        this.message = `${shipmentCount} ${shipmentCount === 1 ? 'shipment' : 'shipments'} and `
          + `${deliveryCount} ${deliveryCount === 1 ? 'delivery' : 'deliveries'} created.`;
        this.refreshRun();
      },
      error: error => this.error = this.errorText(error, 'The scenario could not be approved and applied.')
    });
  }

  cancelRun(): void {
    if (!this.run) return;
    this.service.cancelBulkPlanningRun(this.run.runId, this.companyId()).subscribe({
      next: run => this.applyRun(run),
      error: error => this.error = this.errorText(error, 'Could not cancel the run.')
    });
  }

  refreshRun(): void {
    if (!this.run) return;
    this.service.getBulkPlanningRun(this.run.runId, this.companyId()).subscribe({
      next: run => this.applyRun(run),
      error: error => this.error = this.errorText(error, 'Could not refresh the run.')
    });
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (['APPLIED', 'REVIEW_READY', 'PLANNED', 'ELIGIBLE', 'MATCHED_ELIGIBLE'].includes(status || '')) return 'success';
    if (['QUEUED', 'RUNNING'].includes(status || '')) return 'info';
    if (['PARTIAL', 'UNPLANNED', 'SPLIT', 'NEEDS_VALIDATION', 'MATCHED_BLOCKED', 'AMBIGUOUS'].includes(status || '')) return 'warn';
    if (['FAILED', 'BLOCKED', 'CANCELLED', 'UNMATCHED'].includes(status || '')) return 'danger';
    return 'secondary';
  }

  selectionSummary(): string {
    if (this.selection.mode === 'EXPLICIT_IDS') {
      return `${this.selection.releaseIds?.length ?? 0} explicitly selected releases`;
    }
    const excluded = this.selection.excludedReleaseIds?.length ?? 0;
    return `All releases matching the saved filter${excluded ? `, excluding ${excluded}` : ''}`;
  }

  km(value?: number): string {
    return value == null ? '-' : `${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })} km`;
  }

  duration(value?: number): string {
    if (value == null) return '-';
    const hours = Math.floor(value / 3600);
    const minutes = Math.round((value % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }

  date(value?: string | Date | null): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : '-';
  }

  routeReleaseCount(route: TransportOptimizationPlanRoute): number {
    return route.releaseCount
      ?? new Set((route.stops ?? []).map(stop => stop.releaseId).filter(Boolean)).size;
  }

  routeStopCount(route: TransportOptimizationPlanRoute): number {
    return route.serviceStopCount ?? route.stops?.length ?? 0;
  }

  routeStopGroups(route: TransportOptimizationPlanRoute): RouteStopGroup[] {
    return this.groupPhysicalStops(route.stops ?? []);
  }

  physicalStopDrafts(routeId: number): RouteStopGroup[] {
    return this.groupPhysicalStops(this.stopDrafts.get(routeId) ?? []);
  }

  canMovePhysicalStop(routeId: number, index: number, direction: -1 | 1): boolean {
    const stops = this.physicalStopDrafts(routeId);
    const target = index + direction;
    if (target < 0 || target >= stops.length) return false;
    [stops[index], stops[target]] = [stops[target], stops[index]];
    return this.hasValidPickupPrecedence(stops.flatMap(stop => stop.activities));
  }

  physicalStopReleaseLabels(stop: RouteStopGroup): string {
    const labels = stop.releaseIds.map(releaseId => {
      const release = this.selectedPlan?.releases?.find(item => item.releaseId === releaseId);
      return this.releaseLabel(release ?? { releaseId, owningCompanyId: this.companyId(), sourceType: 'UNKNOWN' });
    });
    return labels.join(', ');
  }

  routeReference(route: TransportOptimizationPlanRoute): string {
    const snapshot = this.selectedPlan?.vehicles?.find(item => item.vehicleSnapshotId === route.vehicleSnapshotId);
    return snapshot?.providerVehicleLabel || route.vehicleLabel || `Route ${route.routeSequence || '-'}`;
  }

  private routeReleaseIds(route: TransportOptimizationPlanRoute): number[] {
    return [...new Set((route.stops ?? []).map(stop => stop.releaseId).filter((id): id is number => !!id))];
  }

  private groupPhysicalStops(stops: TransportOptimizationPlanRouteStop[]): RouteStopGroup[] {
    const groups = new Map<string, RouteStopGroup>();
    for (const stop of [...stops].sort((left, right) => (left.stopSequence ?? 0) - (right.stopSequence ?? 0))) {
      const stopType = (stop.stopType || 'STOP').toUpperCase();
      const physicalKey = this.physicalStopKey(stop);
      const key = `${stopType}|${physicalKey}`;
      const existing = groups.get(key);
      if (existing) {
        existing.activities.push(stop);
        if (stop.releaseId && !existing.releaseIds.includes(stop.releaseId)) existing.releaseIds.push(stop.releaseId);
        existing.releaseCount = existing.releaseIds.length;
      } else {
        const releaseIds = stop.releaseId ? [stop.releaseId] : [];
        groups.set(key, {
          key,
          label: stop.locationName || this.stopTypeLabel(stopType),
          stopType,
          releaseCount: releaseIds.length,
          releaseIds,
          activities: [stop]
        });
      }
    }
    return [...groups.values()];
  }

  private hasValidPickupPrecedence(stops: TransportOptimizationPlanRouteStop[]): boolean {
    const pickupPositions = new Map<number, number>();
    for (let index = 0; index < stops.length; index += 1) {
      const stop = stops[index];
      if (!stop.releaseId) continue;
      if (stop.stopType === 'PICKUP') pickupPositions.set(stop.releaseId, index);
      if (stop.stopType === 'DROPOFF' && (pickupPositions.get(stop.releaseId) ?? Number.MAX_SAFE_INTEGER) >= index) {
        return false;
      }
    }
    return true;
  }

  private compactNumber(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }

  private cleanReleaseReference(value?: string): string {
    return (value || '').replace(/\s*(?:\/|\|)?\s*Leg\s+\d+\s+of\s+\d+\s*$/i, '').trim();
  }

  private projectReference(value?: string): string {
    if (!value) return '';
    const posmProject = value.match(/POSM\/\d{4}\/\d{2}\/\d+/i)?.[0];
    return posmProject || value;
  }

  stopTypeLabel(stopType: string): string {
    if (stopType === 'PICKUP') return 'Pickup';
    if (stopType === 'DROPOFF') return 'Drop-off';
    return 'Stop';
  }

  resourceSourceLabel(source?: string): string {
    if (source === 'AVAILABLE_FIFO') return 'FIFO availability';
    if (source === 'MOBILIZATION') return 'Mobilization';
    return 'Fleet snapshot';
  }

  utilization(used?: number, capacity?: number): string {
    if (used == null || capacity == null || capacity <= 0) return '-';
    return `${Math.round((used / capacity) * 100)}%`;
  }

  private applyRun(run: BulkPlanningRun): void {
    const priorScenarioId = this.selectedScenarioId;
    this.run = run;
    this.selectedScenarioId = run.scenarios?.some(item => item.scenarioId === priorScenarioId)
      ? priorScenarioId
      : run.scenarios?.find(item => item.selected)?.scenarioId
        ?? run.scenarios?.[0]?.scenarioId
        ?? null;
    if (this.selectedScenarioId) this.loadSelectedPlan();
  }

  private loadSelectedPlan(): void {
    const scenario = this.activeScenario;
    if (!scenario?.planId) {
      this.selectedPlan = null;
      return;
    }
    if (!this.run?.runId) return;
    this.service.getBulkPlanningScenarioPlan(this.run.runId, scenario.scenarioId, this.companyId()).subscribe({
      next: plan => this.selectedPlan = plan,
      error: error => this.error = this.errorText(error, 'Could not load scenario routes.')
    });
  }

  createdShipmentLinks(): TransportExecutionLink[] {
    const links = new Map<number, TransportExecutionLink>();
    for (const link of this.appliedExecutionLinks) {
      if (link.shipmentId) links.set(link.shipmentId, link);
    }
    for (const link of this.selectedPlan?.executionLinks ?? []) {
      if (link.shipmentId) links.set(link.shipmentId, link);
    }
    return [...links.values()];
  }

  shipmentLinkLabel(link: TransportExecutionLink): string {
    return link.shipmentNumber || (link.shipmentId ? `Shipment #${link.shipmentId}` : 'Shipment');
  }

  private readSelectionFromQuery(): void {
    const params = this.route.snapshot.queryParamMap;
    const releaseIds = (params.get('releaseIds') || '')
      .split(',')
      .map(value => Number(value))
      .filter(value => Number.isFinite(value) && value > 0);
    const mode = params.get('selectionMode');
    if (mode === 'FILTER_SNAPSHOT') {
      let filter: BulkPlanningSelectionFilter = { statuses: ['READY'] };
      try {
        filter = JSON.parse(params.get('filter') || '{}');
      } catch {
        this.error = 'The release filter snapshot could not be read. Review the selection before running.';
      }
      this.selection = {
        mode: 'FILTER_SNAPSHOT',
        filter,
        excludedReleaseIds: releaseIds
      };
      return;
    }
    if (mode === 'EXPLICIT_IDS' || releaseIds.length) {
      this.selection = { mode: 'EXPLICIT_IDS', releaseIds };
    }
  }

  newProfile(): BulkPlanningProfile {
    return {
      owningCompanyId: this.companyId(),
      name: '',
      code: '',
      active: true,
      fleetScope: 'OWN_AND_SISTER',
      resourceSourcePolicy: 'HYBRID',
      driverRankingPolicy: 'FIFO',
      vehicleRankingPolicy: 'FIFO',
      rankingStrictness: 'SOFT',
      objective: 'SERVICE_THEN_UTILIZATION',
      includeNeighbourZones: false,
      autoMatchEnabled: true,
      matchPriority: 0,
      laneRules: [this.emptyLaneRule()],
      horizonDays: 2,
      defaultStartLocationId: null,
      defaultEndLocationId: null,
      fixedVehicleCost: 1000,
      costPerHour: 1,
      costPerKilometer: 1,
      maximumRouteDurationSeconds: 43200,
      breakDurationSeconds: 1800,
      maximumDrivingSecondsBetweenBreaks: 14400,
      unplannedPenaltyBase: 1000000,
      capacityBufferPercent: 5,
      minimumTargetUtilizationPercent: 60,
      maximumReleasesPerRoute: 8,
      maximumServiceStopsPerRoute: 10,
      maximumFifoCandidates: 50,
      projectAllocationBasis: 'AUTO',
      scheduleTimezone: 'Asia/Riyadh'
    };
  }

  private emptyLaneRule(): BulkPlanningLaneRule {
    return {
      name: '',
      active: true,
      includePickupNeighbours: false,
      includeDropoffNeighbours: false,
      pickupZoneIds: [],
      dropoffZoneIds: []
    };
  }

  private normalizeProfile(profile: BulkPlanningProfile): BulkPlanningProfile {
    return {
      ...profile,
      autoMatchEnabled: profile.autoMatchEnabled ?? false,
      matchPriority: profile.matchPriority ?? 0,
      laneRules: (profile.laneRules ?? []).map(rule => ({
        ...rule,
        pickupZoneIds: [...(rule.pickupZoneIds ?? [])],
        dropoffZoneIds: [...(rule.dropoffZoneIds ?? [])]
      }))
    };
  }

  private hasCoordinates(location: Location): boolean {
    const latitude = Number((location.location as any)?.latitude ?? (location.location as any)?.lat);
    const longitude = Number((location.location as any)?.longitude ?? (location.location as any)?.lng);
    return Number.isFinite(latitude) && Number.isFinite(longitude);
  }

  private locationZoneName(location: Location): string {
    return this.locationZones.find(zone => zone.id === location.locationZoneId)?.name || 'Zone unavailable';
  }

  private physicalStopKey(stop: TransportOptimizationPlanRouteStop): string {
    if (stop.locationId) return `LOC-${stop.locationId}`;
    return `GEO-${Number(stop.latitude || 0).toFixed(6)}-${Number(stop.longitude || 0).toFixed(6)}`;
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }

  private actorCustomerId(): number | undefined {
    return this.session.getCustomer()?.id;
  }

  private routeReason(routeId: number): string | null {
    const reason = this.adjustmentReasons.get(routeId)?.trim();
    if (!reason) {
      this.error = 'Enter an adjustment reason before saving route changes.';
      return null;
    }
    return reason;
  }

  private afterPlanAdjustment(plan: TransportOptimizationPlan, message: string): void {
    this.selectedPlan = plan;
    this.message = message;
    this.validationMessages = [];
    this.loadAdjustmentAudit();
    this.refreshRun();
  }

  private loadAdjustmentAudit(): void {
    if (!this.run || !this.activeScenario || !this.isAdjustedScenario) {
      this.adjustmentAudit = [];
      return;
    }
    this.service.getBulkPlanningAdjustmentAudit(this.run.runId, this.activeScenario.scenarioId,
      this.companyId()).subscribe(items => this.adjustmentAudit = items ?? []);
  }

  private startOfToday(): Date {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }

  private addDays(value: Date, days: number): Date {
    return new Date(value.getTime() + days * 86400000);
  }

  private errorText(error: any, fallback: string): string {
    return error?.error?.message || error?.error?.error || (typeof error?.error === 'string' ? error.error : fallback);
  }
}
